'use client'

import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import {
	arbitrum,
	arbitrumNova,
	aurora,
	avalanche,
	base,
	blast,
	boba,
	bsc,
	celo,
	cronos,
	fantom,
	fraxtal,
	gnosis,
	gravity,
	hyperEvm,
	immutableZkEvm,
	linea,
	mainnet,
	mantle,
	metis,
	mode,
	moonbeam,
	opBNB,
	optimism,
	polygon,
	polygonZkEvm,
	rootstock,
	scroll,
	sei,
	soneium,
	sonic,
	unichain,
	zksync,
	zora,
} from '@reown/appkit/networks'
import { createAppKit } from '@reown/appkit/react'
import { http } from 'viem'

// All EVM chains LI.FI operates on. We hardcode the full set so the deposit
// flow can quote + execute from whatever chain the user's wallet happens to
// be on without tripping a `wallet_addEthereumChain` prompt. Login UX still
// defaults to mainnet (see defaultNetwork below).
const supportedChains = [
	mainnet,
	polygon,
	bsc,
	base,
	arbitrum,
	arbitrumNova,
	optimism,
	avalanche,
	gnosis,
	fantom,
	linea,
	scroll,
	zksync,
	polygonZkEvm,
	moonbeam,
	celo,
	cronos,
	boba,
	mantle,
	blast,
	mode,
	zora,
	aurora,
	metis,
	rootstock,
	sei,
	opBNB,
	fraxtal,
	gravity,
	hyperEvm,
	immutableZkEvm,
	sonic,
	soneium,
	unichain,
] as const

const projectId = process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID ?? ''

const metadata = {
	name: 'Chance',
	description: 'Prediction market aggregator',
	url: 'https://app.chance.cc',
	icons: ['https://app.chance.cc/logos/icon_2.png'],
}

// Create wagmi adapter. AppKit is used ONLY for the wallet-connection modal
// UI — SIWE signing + JWT exchange + AccountKit auth are handled in
// src/components/auth/hooks/use-siwe-auth.ts. No createSIWEConfig here: that
// plugin assumes it owns the full auth lifecycle (session tracking, auto
// re-prompts on account/network change), which fought our JWT-for-AccountKit
// model and caused a parade of edge-case re-auth prompts.
const wagmiAdapter = new WagmiAdapter({
	networks: [...supportedChains],
	projectId,
	transports: Object.fromEntries(supportedChains.map(c => [c.id, http()])),
})

let appKit: ReturnType<typeof createAppKit> | null = null

export function getAppKit() {
	if (typeof window === 'undefined') return null

	if (!appKit) {
		appKit = createAppKit({
			adapters: [wagmiAdapter],
			networks: [...supportedChains],
			defaultNetwork: mainnet,
			projectId,
			metadata,
			features: {
				analytics: false,
				email: false,
				socials: false,
			},
		})
	}

	return appKit
}

export { wagmiAdapter }
